import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import { BlogCard } from "@/components/BlogCard";
import { BlogTagFilter } from "@/components/BlogTagFilter";
import { Search } from "lucide-react";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface BlogTag {
  id: string;
  name: string;
  slug: string;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  author_name: string;
  published_at: string | null;
  created_at: string;
  tags: BlogTag[];
}

export default function Blog() {
  const [searchParams] = useSearchParams();
  const activeTag = searchParams.get('tag');
  const [searchQuery, setSearchQuery] = useState('');
  const { t: seoT } = useStaticTranslation("seo");

  // Fetch published posts
  const { data: posts = [], isLoading: postsLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, cover_image_url, author_name, published_at, created_at')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch all tags
  const { data: allTags = [] } = useQuery({
    queryKey: ['blog-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_tags')
        .select('id, name, slug')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch post-tag mappings
  const { data: postTags = [] } = useQuery({
    queryKey: ['blog-post-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_post_tags')
        .select('post_id, tag_id');
      if (error) throw error;
      return data || [];
    },
  });

  // Merge tags into posts
  const postsWithTags: BlogPost[] = useMemo(() => {
    return posts.map(post => ({
      ...post,
      tags: postTags
        .filter(pt => pt.post_id === post.id)
        .map(pt => allTags.find(t => t.id === pt.tag_id))
        .filter(Boolean) as BlogTag[],
    }));
  }, [posts, postTags, allTags]);

  // Filter by tag and search
  const filteredPosts = useMemo(() => {
    let result = postsWithTags;

    if (activeTag) {
      result = result.filter(post => post.tags.some(t => t.slug === activeTag));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(post =>
        post.title.toLowerCase().includes(q) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(q))
      );
    }

    return result;
  }, [postsWithTags, activeTag, searchQuery]);

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SEO
        title={seoT("blogTitle", "בלוג תכשיטים | אליס")}
        description={seoT("blogDescription", "מאמרים, טיפים והשראה מעולם התכשיטים. גלו מגמות עיצוב, מדריכי קנייה וסיפורים מעולם הזהב והיהלומים.")}
        breadcrumbs={[
          { name: seoT("home", "ראשי"), url: '/' },
          { name: seoT("breadcrumbBlog", "בלוג"), url: '/blog' },
        ]}
      />
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            הבלוג שלנו
          </h1>
          <div className="gold-divider mb-4" />
          <p className="text-muted-foreground max-w-xl mx-auto">
            מאמרים, טיפים והשראה מעולם התכשיטים
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-md mx-auto mb-8">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="חיפוש מאמרים..."
            className="w-full bg-secondary border border-border pr-10 pl-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
          />
        </div>

        {/* Tag Filter */}
        <div className="flex justify-center mb-10">
          <BlogTagFilter tags={allTags} />
        </div>

        {/* Posts Grid */}
        {postsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[16/10] bg-muted mb-4" />
                <div className="h-4 bg-muted w-3/4 mb-2" />
                <div className="h-3 bg-muted w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">
              {searchQuery || activeTag ? 'לא נמצאו מאמרים התואמים לחיפוש' : 'אין מאמרים עדיין'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <BlogCard
                key={post.id}
                title={post.title}
                slug={post.slug}
                excerpt={post.excerpt}
                coverImageUrl={post.cover_image_url}
                publishedAt={post.published_at}
                authorName={post.author_name}
                tags={post.tags}
              />
            ))}
          </div>
        )}
      </main>

      <Footer2 />
    </div>
  );
}
